import BuildIcon from '@mui/icons-material/Build';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import i18next from 'i18next';
import React from 'react';

import { MaintenanceResponse } from '@/api/model/MaintenanceResponse.model.ts';
import bg from '@/assets/img/bg/bg1.png';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface MaintenanceData {
  maintenanceEndDate: string;
  message: string;
}

const MaintenancePage: React.FC = () => {
  const { t } = useTranslationWithPrefix('views.maintenancePage');
  const isPolish = i18next.language.startsWith('pl');
  const theme = useTheme();
  const colors = theme.colors;

  const getMaintenanceData = (): MaintenanceData => {
    try {
      const savedData = sessionStorage.getItem('maintenanceData');
      if (savedData) {
        const data = JSON.parse(savedData) as MaintenanceResponse;
        if (data?.messagePl && data?.messageEn) {
          return {
            maintenanceEndDate: data.maintenanceEndDate || '',
            message: isPolish ? data.messagePl : data.messageEn,
          };
        }
      }
    } catch (error) {
      console.warn('Failed to get maintenance data from sessionStorage:', error);
    }

    return {
      maintenanceEndDate: '',
      message: t('defaultMessage'),
    };
  };

  const maintenanceData = getMaintenanceData();

  const formatEndDate = (dateStr?: string): string | null => {
    if (!dateStr) return null;

    try {
      const date = new Date(dateStr);
      return date.toLocaleString(isPolish ? 'pl-PL' : 'en-US', {
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const endDate = formatEndDate(maintenanceData.maintenanceEndDate);

  return (
    <Box
      sx={{
        alignItems: 'center',
        background: `linear-gradient(90deg, ${colors.bg} 0%, rgba(5, 8, 15, 0.84) 46%, rgba(5, 8, 15, 0.58) 100%), url(${bg})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        display: 'flex',
        minHeight: '100vh',
        px: { md: 8, xs: 2 },
        py: { md: 8, xs: 4 },
        width: '100%',
      }}
    >
      <Stack
        spacing={3}
        sx={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          boxShadow: colors.shadowCard,
          maxWidth: 620,
          p: { md: 4, xs: 3 },
          width: '100%',
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              alignItems: 'center',
              background: colors.accentBg,
              border: `1px solid ${colors.accentBorder}`,
              borderRadius: '14px',
              color: colors.accent,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              width: 48,
            }}
          >
            <BuildIcon />
          </Box>
          <Typography
            sx={{
              color: colors.accent,
              fontSize: 13,
              fontWeight: 800,
              textTransform: 'uppercase',
            }}
          >
            Service status
          </Typography>
        </Stack>

        <Stack spacing={1.5}>
          <Typography
            sx={{
              color: colors.textPrimary,
              fontSize: { md: 34, xs: 28 },
              fontWeight: 800,
              lineHeight: 1.15,
            }}
          >
            {t('maintenanceWork')}
          </Typography>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: 16,
              lineHeight: 1.65,
              maxWidth: 520,
            }}
          >
            {maintenanceData.message}
          </Typography>
        </Stack>

        {endDate && (
          <Stack
            spacing={0.75}
            sx={{
              background: colors.bgCard2,
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              p: 2,
            }}
          >
            <Typography
              sx={{
                color: colors.textSecondary,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {t('expectedCompletion')}
            </Typography>
            <Typography
              sx={{
                color: colors.accent,
                fontSize: 18,
                fontWeight: 800,
              }}
            >
              {endDate}
            </Typography>
          </Stack>
        )}

        <Typography
          sx={{
            color: colors.textSecondary,
            fontSize: 15,
            fontStyle: 'italic',
            lineHeight: 1.6,
          }}
        >
          {t('thankYouMessage')}
        </Typography>
      </Stack>
    </Box>
  );
};

export default MaintenancePage;
